import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { userRoleApi, UserAccess, AccessLevel } from '../utils/api';
import { loginRequest } from './auth';

interface UserInfo {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  tenantId: string;
  accessToken?: string;
}

/** The product areas access is granted against. */
export type Area = 'releaseNotes' | 'wiki';

const RANK: Record<AccessLevel, number> = { None: 0, Reader: 1, Contributor: 2, Admin: 3 };

export type AccessStatus = 'loading' | 'ready' | 'error';

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  /** The caller's access, or null unless accessStatus is 'ready'. */
  access: UserAccess | null;
  /**
   * Whether access is known yet. 'ready' with no entitlements means genuinely
   * not entitled; 'error' means we could not find out. Conflating the two told
   * people they had no access whenever the API hiccupped.
   */
  accessStatus: AccessStatus;
  /** Entitled to something. False means the no-access screen. */
  hasAnyAccess: boolean;
  /** True for Reader and above in that area. */
  canRead: (area: Area) => boolean;
  /** True for Contributor and above: may author in that area. */
  canWrite: (area: Area) => boolean;
  /** True for Admin of that area: may delete and restructure. */
  canAdmin: (area: Area) => boolean;
  /** Manages users. Deliberately not the same as administering an area. */
  isSystemAdmin: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  refreshAccess: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userInfo: null,
  access: null,
  accessStatus: 'loading',
  hasAnyAccess: false,
  canRead: () => false,
  canWrite: () => false,
  canAdmin: () => false,
  isSystemAdmin: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  refreshAccess: async () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] || null;
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [access, setAccess] = useState<UserAccess | null>(null);
  const [accessStatus, setAccessStatus] = useState<AccessStatus>('loading');
  const [isLoading, setIsLoading] = useState(true);

  // Takes no argument: the API answers about the caller it authenticated, not
  // about whoever a header names, so there is nothing to pass.
  //
  // A failure is reported as a failure. It must not read as "no access":
  // guessing generously would restore the bug this replaced, and guessing
  // meanly tells an administrator they have been locked out because a cold
  // start took too long.
  const refreshAccess = async (): Promise<void> => {
    setAccessStatus('loading');
    try {
      // Goes through the shared client so the bearer token is attached.
      const result = await userRoleApi.me();
      if (result.error || !result.data) {
        console.error('Could not resolve access:', result.error);
        setAccess(null);
        setAccessStatus('error');
        return;
      }
      setAccess(result.data);
      setAccessStatus('ready');
    } catch (e) {
      console.error('Could not resolve access:', e);
      setAccess(null);
      setAccessStatus('error');
    }
  };

  useEffect(() => {
    // Safety timeout — never hang forever
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000);

    if (!isAuthenticated || !account) {
      setUserInfo(null);
      setAccess(null);
      setAccessStatus('loading');
      setIsLoading(false);
      clearTimeout(timeout);
      return;
    }

    // Fetch avatar from Microsoft Graph
    const fetchAvatar = async (): Promise<string | undefined> => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account,
        });
        const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        });
        if (photoResponse.ok) {
          return URL.createObjectURL(await photoResponse.blob());
        }
      } catch {
        // Fallback to null if avatar fetch fails
      }
      return undefined;
    };

    let loaded = false;
    const onLoaded = () => {
      if (loaded) return;
      loaded = true;
      setIsLoading(false);
      clearTimeout(timeout);
    };

    fetchAvatar().then((avatarUrl) => {
      setUserInfo({
        id: account.localAccountId,
        name: account.name || 'User',
        email: account.username,
        tenantId: account.tenantId,
        avatar: avatarUrl,
      });
      onLoaded();
    });

    // Resolve what this person may see, on every sign-in.
    void refreshAccess();
  }, [isAuthenticated, account]);

  // Stable identities: these end up in the dependency lists of callers'
  // effects, and a fresh function each render would re-run them forever.
  const atLeast = useCallback(
    (area: Area, level: AccessLevel) => !!access && RANK[access[area]] >= RANK[level],
    [access]
  );
  const canRead = useCallback((area: Area) => atLeast(area, 'Reader'), [atLeast]);
  const canWrite = useCallback((area: Area) => atLeast(area, 'Contributor'), [atLeast]);
  const canAdmin = useCallback((area: Area) => atLeast(area, 'Admin'), [atLeast]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userInfo,
        access,
        accessStatus,
        hasAnyAccess:
          !!access &&
          (RANK[access.releaseNotes] > 0 || RANK[access.wiki] > 0 || access.isSystemAdmin),
        canRead,
        canWrite,
        canAdmin,
        isSystemAdmin: !!access?.isSystemAdmin,
        isLoading,
        login: () => instance.loginRedirect(loginRequest),
        logout: () =>
          instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin }),
        refreshAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
